import React, { useState, useEffect, memo, useMemo } from 'react';
import { Autocomplete, TextField as MuiTextField, Box, Typography, ButtonGroup, Button, MenuItem } from '@mui/material';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import {
  collectRosteredTherapistNames,
  therapistMatchesRosterSet,
  isTherapistSchedulableOnDate,
} from '../utils/rosterUtils';
import {
  parseTimeToMinutes,
  getTreatmentSlotOptions,
  getTreatmentLastEndMinutes,
  getTreatmentTotalMinutes,
  formatMinutesToDisplay,
  bookingNeedsAfterHoursApproval,
  buildAfterHoursConfirmMessage,
} from '../utils/treatmentTiming';
import { authaltFetch, AUTHALT_API_BASE } from '../services/authaltApi';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;

const IndividualBookingForm = memo(function IndividualBookingForm({ 
  initialFormData,
  onSubmit, 
  config, 
  isLoading,
  error,
  success,
  isVisible,
  externalMode = false,
  lockedGuestId = null,
  lockedGuestName = null
}) {
  const [formData, setFormData] = useState({
    ...initialFormData,
    generalInstruction: '',
    treatmentInstruction: '',
    assignedDoctor: ''
  });

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [preferredTimeMode, setPreferredTimeMode] = useState('any'); // any | slot | custom
  const [liveTherapists, setLiveTherapists] = useState([]);
  const [bookingsForDate, setBookingsForDate] = useState([]); // For availability checking
  const [rosterForDate, setRosterForDate] = useState({});
  const [rosterLoadedForDate, setRosterLoadedForDate] = useState('');
  const rosterLoaded = Boolean(formData.date && rosterLoadedForDate === formData.date);

  const [schedulingResult, setSchedulingResult] = useState(null);
  const [guests, setGuests] = useState([]);
  const [allGuests, setAllGuests] = useState([]); // All fetched guests
  const [apiCounts, setApiCounts] = useState({ inHouse: 0, upcoming: 0 }); // Store API-provided counts
  const [guestFilter, setGuestFilter] = useState(() => {
    // Force default to 'inhouse' - migrate from old 'upcoming' default
    const saved = localStorage.getItem('individualBookingGuestFilter');
    if (!saved) {
      localStorage.setItem('individualBookingGuestFilter', 'inhouse');
      return 'inhouse';
    }
    return saved;
  });
  const [guestLookupReady, setGuestLookupReady] = useState(false);
  
  // Guest room and in-room therapy state
  const [guestRoom, setGuestRoom] = useState('');
  const [guestGender, setGuestGender] = useState(''); // Guest gender for automatic gender matching
  const [guestDepartureDate, setGuestDepartureDate] = useState('');
  const [guestRoomTherapyAvailable, setGuestRoomTherapyAvailable] = useState(false);
  const [allRoomsData, setAllRoomsData] = useState({});

  const formResetKey = [
    initialFormData?.guestId || '',
    initialFormData?.date || '',
    externalMode ? 'external' : 'scheduler',
    lockedGuestId || '',
  ].join('|');

  useEffect(() => {
    if (!initialFormData) return;
    setFormData({
      ...initialFormData,
      generalInstruction: initialFormData.generalInstruction || '',
      treatmentInstruction: initialFormData.treatmentInstruction || '',
      assignedDoctor: initialFormData.assignedDoctor || '',
    });
    // A fresh object is passed on every parent render. Reset only when the
    // guest or date changes, so Schedule Treatment does not wipe the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formResetKey]);

  // In external mode, set guest data from locked props
  useEffect(() => {
    if (externalMode && lockedGuestId) {
      setFormData(prev => ({
        ...prev,
        guestId: lockedGuestId,
        guestName: lockedGuestName || lockedGuestId
      }));
    }
  }, [externalMode, lockedGuestId, lockedGuestName]);

  useEffect(() => {
    if (!externalMode || !lockedGuestId || !guestLookupReady) return;
    const match = guests.find((g) => String(g.id || '') === String(lockedGuestId) || String(g.guestId || '') === String(lockedGuestId));
    if (!match) return;
    const guestRoomNumber = match.room || '';
    setGuestRoom(guestRoomNumber);
    setGuestDepartureDate(match.checkOut || '');
    setGuestGender(match.gender || '');
    checkInRoomTherapy(guestRoomNumber);
  }, [externalMode, lockedGuestId, guestLookupReady, guests]);

  // Fetch bookings for the selected date (for therapist availability checking)
  useEffect(() => {
    const fetchBookingsForDate = async () => {
      if (!formData.date) {
        setBookingsForDate([]);
        return;
      }

      try {
        const response = await apiGet(`/api/bookings/${formData.date}`);
        if (response.ok) {
          const bookings = await response.json();
          setBookingsForDate(bookings || []);
        } else {
          setBookingsForDate([]);
        }
      } catch (error) {
        console.error('Failed to fetch bookings for availability check:', error);
        setBookingsForDate([]);
      }
    };

    fetchBookingsForDate();
  }, [formData.date]);

  // Fetch roster assignments for the selected date (to check if therapist is scheduled to work)
  useEffect(() => {
    if (!formData.date) {
      setRosterForDate({});
      setRosterLoadedForDate('');
      return undefined;
    }

    const requestedDate = formData.date;
    let cancelled = false;
    setRosterLoadedForDate('');

    (async () => {
      try {
        const response = await apiGet(
          `/api/roster/assignments?start_date=${requestedDate}&end_date=${requestedDate}`,
        );
        if (cancelled) return;
        if (response.ok) {
          const assignments = await response.json();
          setRosterForDate(assignments || {});
        } else {
          setRosterForDate({});
        }
        setRosterLoadedForDate(requestedDate);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch roster for availability check:', error);
        setRosterForDate({});
        setRosterLoadedForDate(requestedDate);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.date]);

  const rosteredTherapistNames = useMemo(
    () => (formData.date && rosterLoaded
      ? collectRosteredTherapistNames(rosterForDate, formData.date)
      : new Set()),
    [formData.date, rosterForDate, rosterLoaded],
  );

  const filteredTherapistOptions = useMemo(() => {
    const all = Array.isArray(liveTherapists) ? liveTherapists : [];
    const sel = formData.treatment;

    if (formData.date && !rosterLoaded) {
      return [];
    }

    let filtered = all.filter(
      (t) => t && t.name && t.name.toLowerCase() !== 'nan'
        && (!formData.date || isTherapistSchedulableOnDate(t, formData.date)),
    );

    if (sel) {
      filtered = filtered.filter((t) => {
        const skills = Array.isArray(t.skills) ? t.skills : [];
        return skills.includes(sel);
      });
    }

    if (formData.date && rosterLoaded) {
      filtered = filtered.filter((t) => therapistMatchesRosterSet(t.name, rosteredTherapistNames));
    }

    if (formData.date && formData.preferredTime && sel && config && config.treatments) {
      const treatment = config.treatments.find((t) => t.name === sel);
      const serviceMinutes = treatment
        ? (Number(treatment.duration) || 0) + (Number(treatment.preparation_time) || 0)
        : 0;
      const duration = serviceMinutes || 60;

      const toMinutes = (timeStr) => {
        if (!timeStr) return 0;
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
      };

      const addMinutes = (timeStr, mins) => {
        const totalMins = toMinutes(timeStr) + mins;
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      };

      const bookingStart = formData.preferredTime;
      const bookingEnd = addMinutes(bookingStart, duration);

      const timesOverlap = (start1, end1, start2, end2) => {
        const s1 = toMinutes(start1);
        const e1 = toMinutes(end1);
        const s2 = toMinutes(start2);
        const e2 = toMinutes(end2);
        return !(e1 <= s2 || s1 >= e2);
      };

      filtered = filtered.filter((therapist) => {
        const conflicts = bookingsForDate.filter((booking) => {
          const bookingTherapists = booking.therapists || [];
          const therapistNames = bookingTherapists.map((t) => t.name);
          const isTherapistInBooking = therapistNames.includes(therapist.name) || booking.therapist === therapist.name;
          if (!isTherapistInBooking) return false;
          const bStart = booking.startTime || booking.start_time;
          const bEnd = booking.endTime || booking.end_time;
          return timesOverlap(bookingStart, bookingEnd, bStart, bEnd);
        });
        return conflicts.length === 0;
      });
    }

    return filtered.sort((a, b) =>
      (a.first_name || a.name).localeCompare(b.first_name || b.name),
    );
  }, [
    liveTherapists,
    formData.treatment,
    formData.date,
    formData.preferredTime,
    rosterLoaded,
    rosteredTherapistNames,
    bookingsForDate,
    config,
  ]);

  useEffect(() => {
    if (!formData.therapistPreference || !formData.date || !rosterLoaded) return;
    const stillValid = filteredTherapistOptions.some((t) => t.name === formData.therapistPreference);
    if (!stillValid) {
      setFormData((prev) => ({ ...prev, therapistPreference: '' }));
    }
  }, [formData.therapistPreference, formData.date, rosterLoaded, filteredTherapistOptions]);

  // Helper functions for guest filtering
  const toISO = (val) => {
    if (!val) return null;
    const str = String(val).trim();
    if (!str) return null;
    // If already ISO format
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.split('T')[0];
    // Try DD-MM-YYYY or DD/MM/YYYY
    const m1 = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
    if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
    // Fallback: try parsing as date
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return null;
  };

  const getCheckInISO = (g) => toISO(g.checkIn || g.checkin || g.startDate || g.start || g.arrivalDate);
  const getCheckOutISO = (g) => toISO(g.checkOut || g.checkout || g.endDate || g.end || g.departureDate);

  const formatDateRange = (g) => {
    const ci = getCheckInISO(g);
    const co = getCheckOutISO(g);
    if (!ci || !co) return '';
    
    const formatDate = (isoDate) => {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return `${formatDate(ci)} - ${formatDate(co)}`;
  };

  const today = new Date().toISOString().split('T')[0];

  const isInHouse = (g) => {
    const ci = getCheckInISO(g);
    const co = getCheckOutISO(g);
    if (!ci || !co) return false;
    return ci <= today && today <= co;
  };

  const isUpcoming = (g) => {
    const ci = getCheckInISO(g);
    if (!ci) return false;
    return ci > today;
  };

  const validateBookingDateWithinStay = (bookingDate) => {
    if (!bookingDate || !guestDepartureDate) return null;
    const departureISO = getCheckOutISO({
      checkOut: guestDepartureDate,
      checkout: guestDepartureDate,
      departureDate: guestDepartureDate,
    });
    if (!departureISO) return null;
    if (bookingDate > departureISO) {
      const checkoutFormatted = new Date(departureISO).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `Cannot book on ${bookingDate}. The guest's check-out date is ${checkoutFormatted}.`;
    }
    return null;
  };

  // Load rooms data for in-room therapy detection
  useEffect(() => {
    const loadRoomsData = async () => {
      try {
        const response = await apiGet('/api/config');
        if (response.ok) {
          const data = await response.json();
          setAllRoomsData(data.rooms || {});
        }
      } catch (err) {
        console.error('Failed to fetch rooms data:', err);
      }
    };
    loadRoomsData();
  }, []);

  // Load guests for lookup
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        // Fetch all guest types using the new API endpoints with POST
        const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
        let allGuestsData = [];
        let counts = { inHouse: 0, todaysArrival: 0, todaysDeparture: 0, arrivalNext4Days: 0 };
        
        for (const type of types) {
          try {
            const res = await authaltFetch('/guestlistdatav3', {
              type,
              index: 'atmantan'
            });
            if (res.ok) {
              const response = await res.json();
              const categoryKey = type === 'InHouse' ? 'inHouse' :
                                 type === 'TodaysArrival' ? 'todaysArrival' :
                                 type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
              const guestsFromType = response[categoryKey]?.data || [];
              counts[categoryKey] = response[categoryKey]?.count || 0;
              allGuestsData = allGuestsData.concat(guestsFromType);
            }
          } catch (e) {
            console.error(`Failed to fetch ${type}:`, e);
          }
        }
        
        // Store API counts
        setApiCounts({
          inHouse: counts.inHouse,
          upcoming: counts.todaysArrival + counts.arrivalNext4Days
        });
        
        // Remove duplicates by guestId
        const uniqueGuests = Array.from(
          new Map(allGuestsData.map(g => [g.guestId || g.id, g])).values()
        );
        
        // Normalize - keep ALL fields for potential future use
        const list = uniqueGuests.map((g) => ({
          ...g, // Keep all original fields from v3
          id: g.id || g.guestId || g.GuestId || '',
          name: g.name || g.guestName || g.firstName && g.lastName ? `${g.firstName} ${g.lastName}` : g.firstName || '',
          room: g.room || g.roomNo || g.location || '',
          consultingDoctor: g.consultingDoctor || g.doctor || '',
          checkIn: g.checkIn || g.startDate || g.arrivalDate || '',
          checkOut: g.checkOut || g.endDate || g.departureDate || '',
          gender: g.gender || g.Gender || '',  // Extract guest gender for treatment matching
        }));
        
        // Filter out guests without names and sort alphabetically by name
        const sorted = list
          .filter((g) => g.name && g.name.trim() !== '') // Only show guests with names
          .sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setAllGuests(sorted); // Store all guests
      } catch (e) {
        setAllGuests([]);
      } finally {
        setGuestLookupReady(true);
      }
    };
    fetchGuests();
  }, []);

  // Persist guest filter selection to localStorage
  useEffect(() => {
    localStorage.setItem('individualBookingGuestFilter', guestFilter);
  }, [guestFilter]);

  // One-time migration: Reset filter to 'inhouse' as new default
  useEffect(() => {
    const migrated = localStorage.getItem('individualBookingFilterMigrated');
    if (!migrated) {
      // First time after update - set to 'inhouse'
      localStorage.setItem('individualBookingGuestFilter', 'inhouse');
      localStorage.setItem('individualBookingFilterMigrated', 'true');
      setGuestFilter('inhouse');
    }
  }, []); // Run once on mount

  // Filter guests based on selected filter (upcoming/inhouse)
  useEffect(() => {
    if (guestFilter === 'upcoming') {
      setGuests(allGuests.filter(g => isUpcoming(g)));
    } else if (guestFilter === 'inhouse') {
      setGuests(allGuests.filter(g => isInHouse(g)));
    } else {
      setGuests(allGuests);
    }
  }, [guestFilter, allGuests]);

  const checkInRoomTherapy = (roomNumber) => {
    // Check if guest's room has in-room therapy capability
    if (roomNumber && allRoomsData && Object.keys(allRoomsData).length > 0) {
      const guestRoomKey = `Room No:${roomNumber}`;
      
      // Find therapy room matching guest's room location
      const matchingRoom = Object.entries(allRoomsData).find(
        ([roomName, roomData]) => roomData.location === guestRoomKey
      );
      
      if (matchingRoom) {
        console.log(`✅ In-room therapy available: ${matchingRoom[0]} for guest in ${roomNumber}`);
        
        // Auto-set room preference to guest's room therapy
        setFormData(prev => ({
          ...prev,
          roomPreference: matchingRoom[0]
        }));
        setGuestRoomTherapyAvailable(true);
      } else {
        console.log(`ℹ️ No in-room therapy for guest room ${roomNumber}, will use common rooms`);
        setGuestRoomTherapyAvailable(false);
      }
    } else {
      setGuestRoomTherapyAvailable(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'treatment') {
      setPreferredTimeMode('any');
    }
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'treatment' ? { preferredTime: '', avoidTimes: [] } : {}),
    }));

    if (name === 'guestName' && guestLookupReady) {
      const v = (value || '').trim().toLowerCase();
      const match = guests.find((g) => ((g.name || '').trim().toLowerCase() === v) || ((g.id || '').trim().toLowerCase() === v));
      if (match) {
        const guestRoomNumber = match.room || '';
        setGuestRoom(guestRoomNumber);
        setGuestDepartureDate(match.checkOut || '');
        
        setFormData(prev => ({
          ...prev,
          guestName: match.name || value,
          guestId: match.id || prev.guestId,
          assignedDoctor: match.consultingDoctor || match.doctor || match.assignedDoctor || prev.assignedDoctor,
        }));
        
        // Check for in-room therapy
        checkInRoomTherapy(guestRoomNumber);
      }
    }
  };

  const handleGuestRoomChange = (e) => {
    const newRoomNumber = e.target.value;
    setGuestRoom(newRoomNumber);
    
    // Check for in-room therapy when room number is manually entered
    checkInRoomTherapy(newRoomNumber);
  };

  const handleGuestSelect = (e) => {
    const selectedId = e.target.value;
    const match = guests.find((g) => (g.id || '').toString() === selectedId);
    if (match) {
      const guestRoomNumber = match.room || '';
      setGuestRoom(guestRoomNumber);
      setGuestDepartureDate(match.checkOut || '');
      
      // Extract guest gender for automatic gender matching with therapists
      const gender = match.gender || '';
      setGuestGender(gender);
      console.log('Guest gender:', gender);
      
      setFormData(prev => ({
        ...prev,
        guestName: match.name || prev.guestName,
        guestId: match.id || prev.guestId,
        assignedDoctor: match.consultingDoctor || match.doctor || match.assignedDoctor || prev.assignedDoctor,
      }));
      
      // Check for in-room therapy
      checkInRoomTherapy(guestRoomNumber);
    }
  };

  const handleAvoidTimeChange = (time) => {
    if (preferredTimeMode !== 'custom') return;
    setFormData(prev => ({
      ...prev,
      avoidTimes: prev.avoidTimes.includes(time)
        ? prev.avoidTimes.filter(t => t !== time)
        : [...prev.avoidTimes, time]
    }));
  };

  const selectedTreatment = useMemo(
    () => (config.treatments || []).find((treatment) => treatment.name === formData.treatment),
    [config.treatments, formData.treatment]
  );

  const treatmentSlotOptions = useMemo(() => {
    if (!selectedTreatment) return [];

    let options = getTreatmentSlotOptions(selectedTreatment);
    if (formData.preferredTimeOfDay === 'morning') {
      options = options.filter((slot) => (parseTimeToMinutes(slot.value) ?? 0) < 12 * 60);
    } else if (formData.preferredTimeOfDay === 'afternoon') {
      options = options.filter((slot) => (parseTimeToMinutes(slot.value) ?? 0) >= 12 * 60);
    }
    return options;
  }, [selectedTreatment, formData.preferredTimeOfDay]);

  const treatmentTimeSlots = useMemo(
    () => treatmentSlotOptions.map((slot) => slot.value),
    [treatmentSlotOptions],
  );

  const selectedSlotIsAfterHours = useMemo(() => {
    if (!formData.preferredTime) return false;
    const match = treatmentSlotOptions.find((slot) => slot.value === formData.preferredTime);
    if (match) return match.afterHours;
    return bookingNeedsAfterHoursApproval(selectedTreatment, formData.preferredTime);
  }, [formData.preferredTime, treatmentSlotOptions, selectedTreatment]);

  const customPreferredTimeWarning = useMemo(() => {
    if (preferredTimeMode !== 'custom' || !formData.preferredTime || !selectedTreatment) {
      return '';
    }
    if (selectedSlotIsAfterHours) {
      const lastEnd = getTreatmentLastEndMinutes(selectedTreatment);
      return `Runs past normal service end (${formatMinutesToDisplay(lastEnd)}). You will be asked to confirm before booking.`;
    }
    if (treatmentTimeSlots.length === 0) {
      return '';
    }
    if (treatmentTimeSlots.includes(formData.preferredTime)) {
      return '';
    }
    const regularSlots = treatmentSlotOptions
      .filter((slot) => !slot.afterHours)
      .map((slot) => slot.value);
    return `Custom time should match a treatment slot: ${regularSlots.join(', ')} or an after-hours slot.`;
  }, [
    preferredTimeMode,
    formData.preferredTime,
    selectedTreatment,
    treatmentTimeSlots,
    treatmentSlotOptions,
    selectedSlotIsAfterHours,
  ]);

  const resetPreferredTimeSelection = () => {
    setPreferredTimeMode('any');
    setFormData((prev) => ({ ...prev, preferredTime: '', avoidTimes: [] }));
  };

  const handlePreferredTimeSelection = (event) => {
    const value = event.target.value;
    if (!value) {
      resetPreferredTimeSelection();
      return;
    }
    if (value === '__custom__') {
      setPreferredTimeMode('custom');
      setFormData((prev) => ({ ...prev, preferredTime: '', avoidTimes: [] }));
      return;
    }
    setPreferredTimeMode('slot');
    setFormData((prev) => ({ ...prev, preferredTime: value, avoidTimes: [] }));
  };

  const handleCustomPreferredTimeChange = (event) => {
    const value = event.target.value;
    setPreferredTimeMode('custom');
    setFormData((prev) => ({
      ...prev,
      preferredTime: value,
      avoidTimes: treatmentTimeSlots.includes(value) ? [] : prev.avoidTimes,
    }));
  };

  const preferredTimeSelectValue = preferredTimeMode === 'custom'
    ? '__custom__'
    : (preferredTimeMode === 'slot' ? (formData.preferredTime || '') : '');

  useEffect(() => {
    if (
      preferredTimeMode === 'slot'
      && formData.preferredTime
      && !treatmentTimeSlots.includes(formData.preferredTime)
    ) {
      setPreferredTimeMode('any');
      setFormData((prev) => ({ ...prev, preferredTime: '', avoidTimes: [] }));
    }
  }, [treatmentTimeSlots, preferredTimeMode, formData.preferredTime]);

  // Fetch therapists from config endpoint (same as admin panel uses) whenever visible or on mount
  useEffect(() => {
    let isCancelled = false;
    const load = async () => {
      try {
        const r = await apiGet('/api/config');
        const data = await r.json();
        if (!isCancelled) {
          // Convert therapists object to array with skills
          const therapistsDict = data.therapists || {};
          const therapistsList = Object.values(therapistsDict);
          setLiveTherapists(therapistsList);
        }
      } catch (e) {
        console.error('Failed to load therapists:', e);
        if (!isCancelled) setLiveTherapists([]);
      }
    };
    load();
    return () => { isCancelled = true; };
  }, [isVisible]);

  // Debug log for therapists loading
  useEffect(() => {
    if (liveTherapists.length > 0) {
      console.log('Loaded therapists:', liveTherapists.length, 'Sample:', liveTherapists[0]);
    }
  }, [liveTherapists]);

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", 
    "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", 
    "16:00", "16:30", "17:00", "17:30", "18:00"
  ];

  return (
    <div className={`space-y-4 ${isVisible ? 'block' : 'hidden'}`}>
      
      <form onSubmit={(e) => {
        e.preventDefault();
        if (!formData.guestId || !formData.treatment || !formData.date) {
          setSchedulingResult({
            success: false,
            message: 'Please fill in all required fields'
          });
          return;
        }
        const stayError = validateBookingDateWithinStay(formData.date);
        if (stayError) {
          setSchedulingResult({
            success: false,
            message: stayError
          });
          return;
        }

        const needsAfterHours = bookingNeedsAfterHoursApproval(
          selectedTreatment,
          formData.preferredTime,
        );
        if (needsAfterHours) {
          const confirmed = window.confirm(
            buildAfterHoursConfirmMessage(selectedTreatment, formData.preferredTime),
          );
          if (!confirmed) return;
        }

        onSubmit({
          ...formData,
          guestRoom: guestRoom,
          guestGender: guestGender,
          generalInstruction: formData.generalInstruction || '',
          treatmentInstruction: formData.treatmentInstruction || '',
          preferredTime: formData.preferredTime || null,
          allowAfterOperationalEnd: needsAfterHours,
        });
      }} className="space-y-4">
        {/* Guest Filter Toggle */}
        {/* Guest Filter Toggle - Hide in external mode */}
        {!externalMode && (
          <div className="mb-3">
            <ButtonGroup 
              fullWidth 
              variant="outlined" 
              sx={{
                '& .MuiButton-root': {
                  borderColor: '#E4C984',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#E4C984',
                    backgroundColor: 'rgba(228, 201, 132, 0.1)',
                  }
                },
                '& .MuiButton-root.Mui-selected': {
                  backgroundColor: '#E4C984',
                  color: '#fff',
                  borderColor: '#E4C984',
                  '&:hover': {
                    backgroundColor: '#d4b874',
                  }
                }
              }}
            >
              <Button
                className={guestFilter === 'upcoming' ? 'Mui-selected' : ''}
                onClick={() => setGuestFilter('upcoming')}
              >
                Upcoming ({apiCounts.upcoming})
              </Button>
              <Button
                className={guestFilter === 'inhouse' ? 'Mui-selected' : ''}
                onClick={() => setGuestFilter('inhouse')}
              >
                In-house ({apiCounts.inHouse})
              </Button>
            </ButtonGroup>
          </div>
        )}

        {/* Guest Name */}
        <div>
          {externalMode && lockedGuestId ? (
            // External Mode: Show locked guest (read-only)
            <MuiTextField
              label="Guest"
              variant="outlined"
              size="small"
              value={lockedGuestName || lockedGuestId || formData.guestName || ''}
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f8f9fa',
                  '& fieldset': { borderColor: '#e5e7eb' }
                }
              }}
            />
          ) : (
            // Normal Mode: Guest selection Autocomplete
            <Autocomplete
              options={guests}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name || option.id || '';
              }}
              value={guests.find(g => g.id === formData.guestId) || null}
              onChange={(e, newValue) => {
                if (newValue) {
                  handleGuestSelect({ target: { value: newValue.id || '' } });
                } else {
                  // Clear all form data when guest is cleared
                  setFormData({
                    ...initialFormData,
                    guestName: '',
                    guestId: '',
                    treatment: '',
                    therapistPreference: '',
                    roomPreference: '',
                    genderPreference: '',
                    preferredTime: '',
                    preferredTimeOfDay: '',
                    avoidTimes: [],
                    generalInstruction: '',
                    treatmentInstruction: '',
                    assignedDoctor: ''
                  });
                  setGuestRoom('');
                  setGuestGender(''); // Clear guest gender
                  setGuestDepartureDate('');
                  setGuestRoomTherapyAvailable(false);
                }
              }}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  size="small"
                  placeholder="Select Guest"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': { borderColor: '#C7AE6E' },
                      '&.Mui-focused fieldset': { borderColor: '#C7AE6E' }
                    }
                  }}
                />
              )}
              renderOption={(props, option, state) => {
                const dateRange = formatDateRange(option);
                return (
                  <li {...props} key={`guest-${option.id}-${state.index}`}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {option.id}{option.room ? ` • Room ${option.room}` : ''}{dateRange ? ` • ${dateRange}` : ''}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />
          )}
        </div>
        
        {/* Guest ID, Gender, and Room - Single Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: '12px' }}>
          {/* Guest ID */}
          <MuiTextField
            label="Guest ID *"
            variant="outlined"
            size="small"
            name="guestId"
            value={formData.guestId}
            onChange={handleInputChange}
            required
            placeholder="Required"
            InputProps={{
              readOnly: true,
            }}
          />

          {/* Guest Gender */}
          <MuiTextField
            label="Gender"
            variant="outlined"
            size="small"
            name="guestGender"
            value={guestGender || 'Not specified'}
            placeholder="Auto-filled"
            InputProps={{
              readOnly: true,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f9fafb'
              }
            }}
          />

          {/* Guest Room */}
          <MuiTextField
            label="Room"
            variant="outlined"
            size="small"
            name="guestRoom"
            value={guestRoom}
            onChange={handleGuestRoomChange}
            placeholder="e.g., 241"
            InputProps={{
              endAdornment: guestRoomTherapyAvailable && (
                <span className="text-green-600 text-xs font-medium">
                  ✅
                </span>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: guestRoomTherapyAvailable ? '#f0fdf4' : '#ffffff'
              }
            }}
          />
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#C7AE6E] focus:ring-1"
            required
          />
        </div>

        {/* Treatment Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
          <Autocomplete
            options={(config.treatments || [])
              .filter(t => t.short_name) // Only show treatments with short_name
              .sort((a, b) => a.name.localeCompare(b.name))}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.name || '';
            }}
            value={config.treatments?.find(t => t.name === formData.treatment) || null}
            onChange={(e, newValue) => {
              setPreferredTimeMode('any');
              handleInputChange({ target: { name: 'treatment', value: newValue?.name || '' } });
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                size="small"
                placeholder="Select Treatment"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#C7AE6E' },
                    '&.Mui-focused fieldset': { borderColor: '#C7AE6E' }
                  }
                }}
              />
            )}
            renderOption={(props, option, state) => (
              <li {...props} key={`treatment-${option.name}-${state.index}`}>
                <Typography variant="body2">
                  {option.name}
                </Typography>
              </li>
            )}
          />
        </div>

        {/* Therapist Preference */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Therapist Preference</label>
          <Autocomplete
            options={filteredTherapistOptions}
            filterSelectedOptions
            isOptionEqualToValue={(option, value) => option?.name === value?.name}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ') || option.name;
              const details = [option.title, option.gender, option.level].filter(Boolean).join(', ');
              return `${fullName}${details ? ` — ${details}` : ''}`;
            }}
            value={filteredTherapistOptions.find((t) => t.name === formData.therapistPreference) || null}
            onChange={(e, newValue) => {
              handleInputChange({ target: { name: 'therapistPreference', value: newValue?.name || '' } });
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                size="small"
                placeholder="Any Therapist"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#C7AE6E' },
                    '&.Mui-focused fieldset': { borderColor: '#C7AE6E' }
                  }
                }}
              />
            )}
            renderOption={(props, option, state) => {
              const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ') || option.name;
              const details = [option.title, option.gender, option.level].filter(Boolean).join(', ');
              return (
                <li {...props} key={`therapist-${option.name}-${state.index}`}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {fullName}
                    </Typography>
                    {details && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {details}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
          />
          <p className="mt-1 text-xs text-gray-500">
            {(() => {
              if (!formData.treatment) {
                return 'Select a treatment first to filter therapists';
              }

              const trainedCount = liveTherapists.filter((t) => {
                const skills = Array.isArray(t.skills) ? t.skills : [];
                return skills.includes(formData.treatment);
              }).length;

              if (!formData.date) {
                return `${trainedCount} therapist${trainedCount !== 1 ? 's' : ''} trained for this treatment (select date for roster filtering)`;
              }
              if (!rosterLoaded) {
                return 'Loading roster for this date…';
              }
              if (rosteredTherapistNames.size === 0) {
                return `No therapists are rostered on ${formData.date} — create a duty roster first`;
              }
              if (formData.preferredTime) {
                return `✅ ${filteredTherapistOptions.length} therapist${filteredTherapistOptions.length !== 1 ? 's' : ''} with required skills, rostered, and free at selected time`;
              }
              return `✅ ${filteredTherapistOptions.length} therapist${filteredTherapistOptions.length !== 1 ? 's' : ''} with required skills and on duty roster for ${formData.date} (${rosteredTherapistNames.size} rostered that day)`;
            })()}
          </p>
        </div>

        {/* Room Preference */}
        <div>
          <MuiTextField
            select
            label="Room Preference"
            variant="outlined"
            size="small"
            name="roomPreference"
            value={formData.roomPreference}
            onChange={handleInputChange}
            fullWidth
          >
            <MenuItem value="">Any Room</MenuItem>
            {config.rooms && Object.entries(config.rooms)
              .filter(([_, room]) => {
                const t = formData.treatment;
                if (!t) return true;
                const list = Array.isArray(room.availableTreatments) ? room.availableTreatments : [];
                return list.includes(t);
              })
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([roomName, room]) => {
                const location = room.location || '';
                const isGuestRoom = location.startsWith('Room No:');
                const roomNumber = isGuestRoom ? location.replace('Room No:', '').trim() : '';
                const isForThisGuest = isGuestRoom && guestRoom && guestRoom === roomNumber;
                const isDisabled = isGuestRoom && guestRoom && guestRoom !== roomNumber;
                
                const details = [room.cabin_type, location, (room.capacity ? `cap ${room.capacity}` : null)]
                  .filter(Boolean)
                  .join(', ');
                
                return (
                  <MenuItem key={roomName} value={roomName} disabled={isDisabled}>
                    {roomName}{details ? ` — ${details}` : ''}
                    {isForThisGuest ? ' 🏠' : ''}
                    {isDisabled ? ' (Only for room ' + roomNumber + ' guests)' : ''}
                  </MenuItem>
                );
              })}
          </MuiTextField>
        </div>

        {/* General Instruction */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            General Instructions
          </label>
          <textarea
            name="generalInstruction"
            value={formData.generalInstruction}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1"
            style={{ boxShadow: '0 0 0 1px #C7AE6E inset' }}
            rows="3"
            placeholder="Enter any general instructions or notes"
          />
        </div>

        {/* Treatment Instruction */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Treatment Instructions
          </label>
          <textarea
            name="treatmentInstruction"
            value={formData.treatmentInstruction}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1"
            style={{ boxShadow: '0 0 0 1px #C7AE6E inset' }}
            rows="3"
            placeholder="Enter specific instructions for the treatment"
          />
        </div>

        {/* Advanced Options Accordion */}
        <div className="mt-4 border rounded-md">
          <button
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            className="w-full px-4 py-2 text-left text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 rounded-t-md flex justify-between items-center"
          >
            <span>Advanced Options</span>
            <svg
              className={`h-5 w-5 transform transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          
          {isAdvancedOpen && (
            <div className="p-4 space-y-4 border-t">
              {/* Time of Day */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Time of Day</label>
                <select
                  name="preferredTimeOfDay"
                  value={formData.preferredTimeOfDay}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#C7AE6E] focus:ring-1"
                >
                  <option value="">Any Time</option>
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                </select>
              </div>

              {/* Preferred Time / Treatment slots */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Preferred Time</label>
                <select
                  value={preferredTimeSelectValue}
                  onChange={handlePreferredTimeSelection}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#C7AE6E] focus:ring-1"
                >
                  <option value="">Any time</option>
                  {treatmentSlotOptions.length > 0 && (
                    <optgroup label="Treatment slots">
                      {treatmentSlotOptions.map((slot) => (
                        <option key={slot.value} value={slot.value}>
                          {slot.value}{slot.afterHours ? ' (after hours)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <option value="__custom__">Custom time...</option>
                </select>
                {preferredTimeMode === 'custom' && (
                  <input
                    type="time"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleCustomPreferredTimeChange}
                    className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#C7AE6E] focus:ring-1"
                  />
                )}
                {customPreferredTimeWarning && (
                  <p className="mt-1 text-xs text-amber-700 font-medium">
                    {customPreferredTimeWarning}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {!formData.treatment
                    ? 'Select a treatment to see its configured start times.'
                    : treatmentSlotOptions.length > 0
                      ? `Treatment slots from Admin for ${formData.treatment}${formData.preferredTimeOfDay ? ` (${formData.preferredTimeOfDay})` : ''}. After-hours slots run past the normal service end time and require confirmation.`
                      : 'No treatment slots configured in Admin for this selection. Use Custom time or set allowed start times under Admin → Services.'}
                </p>
              </div>

              {/* Gender Preference */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Therapist Gender</label>
                <select
                  name="genderPreference"
                  value={formData.genderPreference}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#C7AE6E] focus:ring-1"
                >
                  <option value="">No Preference</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                <p className="mt-1 text-xs text-amber-600">
                  ⚠️ For gender-sensitive treatments, therapist gender will automatically match guest gender for safety, regardless of this preference.
                </p>
              </div>

              {preferredTimeMode === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Avoid Time Slots</label>
                  <p className="mt-1 mb-2 text-xs text-gray-500">
                    Only used with a custom preferred time. Select times the scheduler should skip.
                  </p>
                  <div className="relative">
                    <select
                      multiple
                      size={5}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-1 max-h-40 overflow-y-auto"
                      style={{ height: '160px' }}
                    >
                      {timeSlots
                        .filter((time) => time !== formData.preferredTime)
                        .map((time) => (
                        <option
                          key={time}
                          value={time}
                          className="p-2 hover:bg-gray-100 cursor-pointer flex items-center"
                          onClick={() => handleAvoidTimeChange(time)}
                        >
                          <input
                            type="checkbox"
                            checked={formData.avoidTimes.includes(time)}
                            onChange={() => handleAvoidTimeChange(time)}
                            className="mr-2 h-4 w-4 rounded border-gray-300"
                            style={{ accentColor: '#C7AE6E' }}
                          />
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formData.avoidTimes.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2 max-h-20 overflow-y-auto">
                      {formData.avoidTimes.map(time => (
                        <span 
                          key={time} 
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                        >
                          {time}
                          <button
                            type="button"
                            onClick={() => handleAvoidTimeChange(time)}
                            className="ml-1 inline-flex text-red-600 hover:text-red-900 focus:outline-none"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scheduling Results */}
        {schedulingResult && (
          <div className={`p-4 rounded-md ${
            schedulingResult.success ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <p className={`text-sm ${
              schedulingResult.success ? 'text-green-700' : 'text-red-700'
            }`}>
              {schedulingResult.message}
            </p>
            {schedulingResult.success && (
              <div className="mt-2 text-sm text-gray-600">
                <p>Time: {schedulingResult.startTime} - {schedulingResult.endTime}</p>
                <p>Room: {schedulingResult.room}</p>
                <p>
                  Therapist{schedulingResult.therapistsCount > 1 ? 's' : ''}: {(() => {
                    const therapists = schedulingResult.therapists || [];
                    const therapistsCount = schedulingResult.therapistsCount || 1;
                    if (therapistsCount > 1) {
                      const allNames = therapists.map(t => t.name).join(', ');
                      return `${allNames} (${therapistsCount} therapists)`;
                    }
                    return schedulingResult.therapist;
                  })()}
                </p>
                {schedulingResult.generalInstruction && (
                  <p>General Instructions: {schedulingResult.generalInstruction}</p>
                )}
                {schedulingResult.treatmentInstruction && (
                  <p>Treatment Instructions: {schedulingResult.treatmentInstruction}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="p-4 bg-green-50 text-green-700 rounded-md">
            {success}
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-4">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#171717] 
              ${isLoading ? 'bg-[#e9d9a2] cursor-not-allowed' : 'bg-[#E4C984] hover:bg-[#cbb16f] focus:outline-none focus:ring-2 focus:ring-offset-2'}`}
          >
            {isLoading ? 'Scheduling...' : 'Schedule Treatment'}
          </button>
        </div>
      </form>
    </div>
  );
});

export default IndividualBookingForm;
